import { chargeMappingService } from './chargeMappingService';
import { chargeMapService } from './chargeMapService';
import { buildMapContext } from '../lib/comparisonSections';
import { billReport, type BillReport } from '../lib/billVerdict';
import { effectiveInvoiceResult } from '../lib/effectiveInvoice';
import type { ChargeMapRow } from '../utils/chargeMap';
import type { NameAliases } from '../utils/nameAliases';
import type { BvBill } from '../types/billTypes';

// Charge maps for `billReport`, shared by `useBillReports` (hooks/useBillVerdicts)
// and one-off exports that run outside a component (a report batch's MultiSite
// XLSX). Retailer name → its charge map rows, cached for the session: a list
// page and the bill it opens read the same few retailers.
const cache = new Map<string, Promise<ChargeMapRow[]>>();

export const loadChargeMap = (retailerName: string): Promise<ChargeMapRow[]> => {
  let hit = cache.get(retailerName);
  if (!hit) {
    hit = (async () => {
      const res = await chargeMappingService.resolve(retailerName);
      const retailerIds = res.success ? res.data?.group?.map((r) => r.id) ?? [] : [];
      const mapRes = await chargeMapService.forRetailer(retailerIds);
      return mapRes.success ? mapRes.data ?? [] : [];
    })().catch((err) => {
      console.error('Charge map load failed:', err);
      cache.delete(retailerName);
      return [];
    });
    cache.set(retailerName, hit);
  }
  return hit;
};

export const retailerOf = (bill: Partial<BvBill>): string | null =>
  bill.site?.current_retailer || effectiveInvoiceResult(bill)?.extracted_retailer || null;

/** `billReport` for one bill, given the loaded maps — what `useBillReports` returns per bill. */
export const reportWithMaps = (
  bill: Partial<BvBill>,
  maps: Record<string, ChargeMapRow[]>,
  aliases: NameAliases | undefined,
  tolerancePct?: number,
): BillReport | null => {
  const name = retailerOf(bill);
  const rows = name ? maps[name] ?? [] : [];
  const ar = effectiveInvoiceResult(bill);
  return billReport(bill, buildMapContext(rows, bill.site?.bv_extn, ar?.lines ?? [], aliases), tolerancePct);
};

/**
 * `useBillReports` outside React: waits for every retailer's charge map and the
 * distributor aliases, then judges each bill the way Bill Runs does.
 */
export const loadBillReporter = async (
  bills: Partial<BvBill>[],
  tolerancePct?: number,
): Promise<(bill: Partial<BvBill>) => BillReport | null> => {
  const names = [...new Set(bills.map(retailerOf).filter((n): n is string => !!n))];
  const [pairs, aliases] = await Promise.all([
    Promise.all(names.map(async (n) => [n, await loadChargeMap(n)] as const)),
    chargeMapService.distributorAliases(),
  ]);
  const maps = Object.fromEntries(pairs);
  return (bill) => reportWithMaps(bill, maps, aliases, tolerancePct);
};
